import React from 'react';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import { getIntl } from '../../../../../utils/intl';
import getName from '../../../../../utils/getName';
import { useGoBack } from '../../../../../utils/appNavigationNames';
import useUser from '../../../../../hooks/api/useUser';
import { useSignOutAndResetApp } from '../../../../../hooks/useSignOutAndResetApp';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../v2BaseComponents/InfoIcon';
import Text from '../../../../v2BaseComponents/Text';
import { Checkbox } from '../../../../v2BaseComponents/Inputs';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { flushAnalyticsEvents } from '../../../../../utils/analytics/queue';
import { recordSessionStop } from '../../../../../utils/analytics/record';

const intl = getIntl('account-deletion-confirmation', {
    header: 'You Are About to Delete Your Account',
    noProfilesContent: 'This will delete your account and all of your data.  Are you sure you want to continue?',
    delete: 'Yes, Delete Account',
    cancel: 'Cancel',
    profilesContent:
        "This will delete all your account data including the profiles you are the owner of.  This cannot be undone.  It is suggested that you transfer the ownership of these profiles to another trusted collaborator.  If you'd wish to continue, please confirm the deletion of each profile by checking each profile in the list below:",
    profileDeleteMessage: "Delete {name}'s profile",
});

export type ConsumerAccountDeletionConfirmationScreenOwnProps = {};

export type ConsumerAccountDeletionConfirmationScreenProps = ConsumerAccountDeletionConfirmationScreenOwnProps;

const ConsumerAccountDeletionConfirmationScreen: React.FC<ConsumerAccountDeletionConfirmationScreenProps> = (
    _props,
) => {
    const { isLoadingProfiles, medicalProfiles } = useMedicalProfiles();
    const [checkedProfileIds, setCheckedProfileIds] = React.useState<string[]>([]);
    const goBack = useGoBack();
    const { signOutAndResetApp, isResetting } = useSignOutAndResetApp();
    const { deleteUser, isDeletingUser } = useUser({
        onDeleteSuccess: signOutAndResetApp,
    });
    const record = useRecordEvent();
    // Analytics are sent before deletion starts, which takes a network round trip. Treat that as part of
    // the deletion so the buttons are gone for its duration.
    const [isSendingDeletionAnalytics, setIsSendingDeletionAnalytics] = React.useState(false);

    const getIsDeleteEnabled = () => {
        if (isLoadingProfiles) return false;

        if (!medicalProfiles?.length) return true;

        return medicalProfiles.every((profile) => checkedProfileIds.includes(profile.medicalProfileId));
    };

    return (
        <AnimateLoadedContent isLoading={isDeletingUser || isSendingDeletionAnalytics || isLoadingProfiles}>
            {() => (
                <ActionButtonLayout
                    whiteBackground
                    scrollable
                    actions={[
                        {
                            icon: 'X',
                            title: intl('cancel'),
                            isDisabled: isDeletingUser,
                            onPress: goBack,
                            variant: 'secondary',
                            testID: 'cancel-delete-account-button',
                        },
                        {
                            icon: 'Trash2',
                            title: intl('delete'),
                            isDisabled: !getIsDeleteEnabled() || isResetting || isSendingDeletionAnalytics,
                            onPress: async () => {
                                setIsSendingDeletionAnalytics(true);
                                record('deleteAccount');
                                // Close the session here too, so its stop event travels in the same
                                // authenticated batch. Deleting the user removes the Cognito identity and
                                // the lookup the analytics route resolves the user through, so nothing
                                // queued after this point can be posted.
                                recordSessionStop();
                                await flushAnalyticsEvents();
                                deleteUser();
                                setIsSendingDeletionAnalytics(false);
                            },
                            variant: 'alert',
                            testID: 'delete-account-button',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <ContentStack>
                            <InfoIcon
                                alertIconProps={{
                                    name: 'AlertTriangle',
                                    variant: 'error',
                                }}
                                title={intl('header')}
                            />
                            <Text variant="body-1">{intl('profilesContent')}</Text>
                            <Stack vertical fullWidth space={1}>
                                {medicalProfiles
                                    .map((profile) => ({
                                        id: profile.medicalProfileId,
                                        name: getName(profile),
                                    }))
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((profile) => (
                                        <Checkbox
                                            key={profile.id}
                                            value={checkedProfileIds.includes(profile.id)}
                                            onChange={(value) => {
                                                if (value) {
                                                    setCheckedProfileIds([...checkedProfileIds, profile.id]);
                                                } else {
                                                    setCheckedProfileIds(
                                                        checkedProfileIds.filter((id) => id !== profile.id),
                                                    );
                                                }
                                            }}
                                            title={intl('profileDeleteMessage', { name: profile.name })}
                                            testID={`delete-profile-checkbox-${profile.name}`}
                                        />
                                    ))}
                            </Stack>
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ConsumerAccountDeletionConfirmationScreen);
